import type { ApiTypes, TrackableEventTypes } from "@fluid-app/api-client";

/**
 * Default regex pattern for extracting attribution from the URL path
 * This pattern captures the segment before shop/enrollments/post/library/page paths
 * while excluding paths that start with "home/"
 *
 * Examples:
 * - https://www.fluid.app/70738d/shop/apple-vision-pro → "70738d" is captured
 * - https://www.fluid.app/influencer-name/library → "influencer-name" is captured
 * - https://www.fluid.app/home/shop → No match (excluded by negative lookahead)
 */
export const DEFAULT_PATH_ATTRIBUTION_PATTERN =
  "/(?!home/)([^/]+)/(?:shop|enrollments|post|library|page)";

/**
 * Default regex pattern for extracting attribution from the URL subdomain
 * This pattern captures the first subdomain segment from a fully qualified domain
 *
 * Features:
 * - Handles optional http/https protocols
 * - Extracts the first subdomain segment
 * - Works with both regular domains and localhost
 * - Handles optional port numbers
 *
 * Examples:
 * - john.example.com → "john" is captured
 * - https://jane.store.example.com → "jane" is captured
 * - affiliate.mystore.com:3000 → "affiliate" is captured
 * - john.localhost:8080 → "john" is captured
 */
export const DEFAULT_SUBDOMAIN_ATTRIBUTION_PATTERN =
  "^(?:https?:\\/\\/)?([^.]+)\\.(?:.+?\\..+|localhost)(?::\\d+)?$";

/**
 * Configuration options for FairShare
 */
export interface FairshareConfig {
  /** Fluid shop identifier */
  fluidShop: string;

  /** Debug mode */
  debug?: boolean;

  /** Attribution settings (Required) */
  attributionSettings?: AttributionSettings;

  /** Custom API endpoint for all Fluid API requests (e.g., "https://api.staging.fluid.app") */
  apiEndpoint?: string;

  /** Maximum number of events to send in a single batch */
  batchSize?: number;

  /** Time in ms to wait before sending a batch */
  batchInterval?: number;

  /** Maximum number of events to store in the queue */
  maxQueueSize?: number;

  /** Interval in ms for automatically flushing the event queue (0 to disable) */
  flushIntervalMs?: number;
}

/**
 * Attribution settings for FairShare
 *
 * At least one attribution pattern (path or subdomain) must be provided.
 * If neither is provided, the default patterns will be used.
 */
export type AttributionSettings =
  | {
      /**
       * The regex pattern to use for extracting attribution from the URL path. Must include a capture group.
       * Works with both standard paths and hash-based routes (everything after # in the URL).
       *
       * Default pattern: "/(?!home/)([^/]+)/(?:shop|enrollments|post|library|page)"
       *
       * Examples with default pattern:
       * - https://www.fluid.app/70738d/shop/apple-vision-pro → "70738d" is captured
       * - https://www.fluid.app/influencer-name/library → "influencer-name" is captured
       * - https://www.fluid.app/home/shop → No match (excluded by negative lookahead)
       */
      pathAttributionPattern: string;

      /**
       * The regex pattern to use for extracting attribution from the URL subdomain. Must include a capture group.
       * The pattern can handle various domain formats including those with ports and localhost development.
       *
       * Default pattern: "^(?:https?:\\/\\/)?([^.]+)\\.(?:.+?\\..+|localhost)(?::\\d+)?$"
       *
       * Examples with default pattern:
       * - john.example.com → "john" is captured
       * - https://jane.store.example.com → "jane" is captured
       * - affiliate.mystore.com:3000 → "affiliate" is captured
       * - john.localhost:8080 → "john" is captured
       */
      subdomainAttributionPattern?: string;
    }
  | {
      /**
       * The regex pattern to use for extracting attribution from the URL path. Must include a capture group.
       * Works with both standard paths and hash-based routes (everything after # in the URL).
       *
       * Default pattern: "/(?!home/)([^/]+)/(?:shop|enrollments|post|library|page)"
       *
       * Examples with default pattern:
       * - https://www.fluid.app/70738d/shop/apple-vision-pro → "70738d" is captured
       * - https://www.fluid.app/influencer-name/library → "influencer-name" is captured
       * - https://www.fluid.app/home/shop → No match (excluded by negative lookahead)
       */
      pathAttributionPattern?: string;

      /**
       * The regex pattern to use for extracting attribution from the URL subdomain. Must include a capture group.
       * The pattern can handle various domain formats including those with ports and localhost development.
       *
       * Default pattern: "^(?:https?:\\/\\/)?([^.]+)\\.(?:.+?\\..+|localhost)(?::\\d+)?$"
       *
       * Examples with default pattern:
       * - john.example.com → "john" is captured
       * - https://jane.store.example.com → "jane" is captured
       * - affiliate.mystore.com:3000 → "affiliate" is captured
       * - john.localhost:8080 → "john" is captured
       */
      subdomainAttributionPattern: string;
    };

export interface CheckoutStartedEventWithData {
  eventName: (typeof TrackableEventTypes)["CHECKOUT_STARTED"];
  data: ApiTypes["checkout-started"]["request"];
}

export interface PageVisitEventWithData {
  eventName: (typeof TrackableEventTypes)["PAGE_VISIT"];
  data: ApiTypes["page-visit"]["request"];
}

export type TrackableEventWithData =
  | CheckoutStartedEventWithData
  | PageVisitEventWithData;
